/**
 * RowExpansionTemplate Component
 * 
 * A specialized template component for rendering expandable row content in DataTable components.
 * This component creates a nested sub-table within expanded rows, allowing for hierarchical
 * data display with parent-child relationships.
 * 
 * Purpose:
 * - Provides expandable row functionality for parent DataTable components
 * - Renders nested DataTable with dynamic column configuration
 * - Handles empty states and missing data gracefully
 * - Maintains consistent styling and responsive behavior
 * 
 * Features:
 * - Dynamic sub-table column generation via DynamicSubTable component
 * - Configurable responsive layout options 
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Parent row data object containing sub-table data
 * @param {string} props.bundleSubGroup - Property name in data object that contains sub-table array
 * @param {Array} props.subColDatas - Column configuration array for sub-table columns
 * @param {string} props.className - CSS class name for the expansion container
 * @param {string} props.responsiveLayout - PrimeReact responsive layout mode ('scroll' | 'stack')
 * @param {ReactNode} props.header - Optional header content for the sub-table 
 * 
 * @returns {JSX.Element|null} Rendered expansion template or null if no data

 * */

import React from 'react';
import { DataTable } from 'primereact/datatable';
import DynamicSubTable from './DynamicSubTable';
import PropTypes from 'prop-types';
import {NoSubTableDataDiv} from './DataTable.styled';

const RowExpansionTemplate = ({ 
    data, 
    bundleSubGroup, 
    subColDatas,
    className = 'data-subtable',
    responsiveLayout = 'scroll',
    header = null,
    ...otherDataTableProps 
}) => {
    // Guard clause for missing data
    if (!data || !bundleSubGroup || !data[bundleSubGroup]) {
        return null;
    }

    const subTableData = data[bundleSubGroup];

    // Guard clause for empty data
    if (!Array.isArray(subTableData) || subTableData.length === 0) {
        return (
            <div className={className}>
               <NoSubTableDataDiv>
                No sub-table data available</NoSubTableDataDiv>
            </div>
        );
    }

    return (
        <div className={className}>
            <DataTable
                value={subTableData}
                header={header}
                responsiveLayout={responsiveLayout}
                {...otherDataTableProps}
            >
                <DynamicSubTable subColDatas={subColDatas} />
            </DataTable>
        </div>
    );
};

RowExpansionTemplate.propTypes = {
    data: PropTypes.object.isRequired,
    bundleSubGroup: PropTypes.string.isRequired,
    subColDatas: PropTypes.array,
    className: PropTypes.string,
    responsiveLayout: PropTypes.string,
    header: PropTypes.node
};

RowExpansionTemplate.defaultProps = {
    className: 'data-subtable',
    responsiveLayout: 'scroll',
    header: null,
    subColDatas: []
};

export default RowExpansionTemplate;